import { useEffect, useState } from 'react';
import { Plus, Building2, Save } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/Components/ui/Button';
import { Card, CardHead } from '@/Components/ui/Card';
import { Input } from '@/Components/ui/Input';
import { Select } from '@/Components/ui/Select';
import { Checkbox } from '@/Components/ui/Checkbox';
import { PageHeader } from '@/Components/data/PageHeader';
import { EmptyState } from '@/Components/data/EmptyState';
import { StatusBadge } from '@/Components/ui/Badge';
import { TableActions } from '@/Components/ui/TableActions';
import { Field } from '@/Components/forms/Field';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/Components/ui/Dialog';
import empresaService, { type Empresa, type EmpresaInput } from '@/services/empresaAdmin';

const FORM_VAZIO: EmpresaInput = {
  social_name: '',
  trade_name: '',
  cnpj: '',
  phone: '',
  email: '',
  is_active: true,
  menu_position: 'lateral',
};

/**
 * Pagina "Empresas" (rota raiz /empresas, distinta de /admin/empresas).
 * Equivalente a EmpresaPage.vue: usa o mesmo endpoint EmpresaController
 * (que ja filtra por empresas do proprio usuario quando nao e' staff),
 * mas com um formulario mais simples (sem gestao de licenca/pagamentos,
 * que e' exclusiva do painel administrativo).
 */
export default function Empresas() {
  const navigate = useNavigate();
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [loading, setLoading] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [dialog, setDialog] = useState(false);
  const [editando, setEditando] = useState(false);
  const [form, setForm] = useState<EmpresaInput & { id?: string }>(FORM_VAZIO);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);

  const carregar = async () => {
    setLoading(true);
    try {
      const data = await empresaService.listar();
      setEmpresas(data);
    } catch (error) {
      console.error('Erro ao carregar empresas', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar();
  }, []);

  const abrirNova = () => {
    setForm(FORM_VAZIO);
    setLogoPreview(null);
    setEditando(false);
    setDialog(true);
  };

  const editar = (item: Empresa) => {
    setForm({ ...item });
    setLogoPreview(item.logo || null);
    setEditando(true);
    setDialog(true);
  };

  const onLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500 * 1024) {
      alert('Arquivo muito grande. Máximo permitido: 500KB');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result as string;
      setForm({ ...form, logo: base64 });
      setLogoPreview(base64);
    };
    reader.readAsDataURL(file);
  };

  const salvar = async () => {
    if (!form.social_name || !form.cnpj) return;
    setSalvando(true);
    try {
      if (form.id) {
        await empresaService.atualizar(form.id, form);
      } else {
        await empresaService.criar(form);
      }
      setDialog(false);
      await carregar();
    } catch (error) {
      console.error('Erro ao salvar empresa', error);
    } finally {
      setSalvando(false);
    }
  };

  const deletar = async (item: Empresa) => {
    try {
      await empresaService.excluir(item.id);
      await carregar();
    } catch (error) {
      console.error('Erro ao excluir empresa', error);
      throw error;
    }
  };

  return (
    <div className="nx-page">
      <PageHeader
        icon={<Building2 size={21} strokeWidth={2} />}
        title="Empresas"
        description="Cadastro das empresas vinculadas ao sistema."
        actions={
          <Button type="button" onClick={() => navigate('/empresas/novo')}>
            <Plus size={16} strokeWidth={2.2} />
            Nova Empresa
          </Button>
        }
      />

      <Card className="nx-page-card nx-sngpc-listing-card">
        <CardHead title="Listagem" />

        {!loading && empresas.length === 0 ? (
          <EmptyState message="Nenhuma empresa encontrada." />
        ) : (
          <div className="nx-table-wrap">
            <table className="nx-table">
              <thead>
                <tr>
                  <th>Razão Social</th>
                  <th>Nome Fantasia</th>
                  <th>CNPJ</th>
                  <th>Status</th>
                  <th className="nx-col-acoes">Ações</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={5} className="nx-table-loading">Carregando…</td>
                  </tr>
                ) : (
                  empresas.map((item) => (
                    <tr key={item.id}>
                      <td>{item.social_name}</td>
                      <td>{item.trade_name || '-'}</td>
                      <td>{item.cnpj}</td>
                      <td>
                        <StatusBadge label={item.is_active ? 'Ativa' : 'Inativa'} tone={item.is_active ? 'success' : 'danger'} />
                      </td>
                      <td className="nx-col-acoes">
                        <TableActions
                          onEdit={() => navigate(`/empresas/${item.id}/editar`)}
                          onDelete={() => deletar(item)}
                          deleteTitle="Excluir empresa"
                          deleteDescription={`Tem certeza de que deseja excluir a empresa "${item.social_name}"? Esta ação não poderá ser desfeita.`}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Dialog open={dialog} onOpenChange={(next) => !salvando && setDialog(next)}>
        <DialogContent className="nexus-dialog--wide nx-sngpc-edit-dialog">
          <form onSubmit={(event) => { event.preventDefault(); void salvar(); }}>
          <DialogHeader>
            <DialogTitle>{editando ? 'Editar Empresa' : 'Nova Empresa'}</DialogTitle>
            <DialogDescription>
              {editando ? 'Atualize os dados cadastrais da empresa.' : 'Preencha os dados para incluir uma nova empresa.'}
            </DialogDescription>
          </DialogHeader>

          <div className="nexus-dialog-body">
            <div className="mb-4 flex flex-col items-center gap-2">
              <div className="flex h-24 w-24 items-center justify-center overflow-hidden rounded-full bg-slate-100">
                {logoPreview ? <img src={logoPreview} alt="Logo" className="h-full w-full object-cover" /> : <Building2 className="h-10 w-10 text-slate-400" />}
              </div>
              <label className="cursor-pointer rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50">
                {logoPreview ? 'Alterar Logo' : 'Carregar Logo'}
                <input type="file" accept="image/*" className="hidden" onChange={onLogoChange} />
              </label>
              <p className="text-[11px] text-slate-400">Formatos: PNG, JPG. Máximo: 500KB</p>
            </div>

            <div className="nx-form-grid">
              <Field label="Razão Social *" htmlFor="empresa-social-name" className="nx-sngpc-field--full">
                <Input
                  id="empresa-social-name"
                  value={form.social_name || ''}
                  onChange={(e) => setForm({ ...form, social_name: e.target.value })}
                  required
                />
              </Field>
              <Field label="Nome Fantasia" htmlFor="empresa-trade-name" className="nx-sngpc-field--full">
                <Input
                  id="empresa-trade-name"
                  value={form.trade_name || ''}
                  onChange={(e) => setForm({ ...form, trade_name: e.target.value })}
                />
              </Field>
              <Field label="CNPJ *" htmlFor="empresa-cnpj">
                <Input
                  id="empresa-cnpj"
                  value={form.cnpj || ''}
                  onChange={(e) => setForm({ ...form, cnpj: e.target.value })}
                  required
                />
              </Field>
              <Field label="E-mail" htmlFor="empresa-email">
                <Input
                  id="empresa-email"
                  value={form.email || ''}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              </Field>
              <Field label="Telefone" htmlFor="empresa-phone">
                <Input
                  id="empresa-phone"
                  value={form.phone || ''}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                />
              </Field>
              <Field label="Posição do Menu" htmlFor="empresa-menu-position">
                <Select
                  id="empresa-menu-position"
                  value={form.menu_position || 'lateral'}
                  onChange={(e) => setForm({ ...form, menu_position: e.target.value })}
                >
                  <option value="lateral">Lateral (Padrão)</option>
                  <option value="top">Superior (Topo)</option>
                </Select>
              </Field>
              <label className="nx-sngpc-checkbox-field">
                <Checkbox
                  checked={!!form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                />
                <span>Ativa</span>
              </label>
            </div>
          </div>

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="ghost" disabled={salvando}>Cancelar</Button>
            </DialogClose>
            <Button type="submit" loading={salvando} loadingText="Salvando...">
              <Save size={16} /> Salvar
            </Button>
          </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
